import { authWithInitData } from '../api/endpoints'
import { useAuthStore, type AuthMode, type User } from './store'
import { logger } from '../utils/logger'

/**
 * Универсальный сервис авторизации для Telegram WebApp и браузера
 * 
 * Поддерживает два режима:
 * 1. Telegram Mode - авторизация через initData (Telegram WebApp API)
 * 2. Browser Mode - авторизация через web_token (URL параметр)
 */
export class AuthService {
  private authStore = useAuthStore.getState()

  /**
   * Извлекает auth_date из initData (в секундах UNIX)
   */
  private getAuthDateFromInitData(initData: string): number | null {
    try {
      const params = new URLSearchParams(initData)
      const authDateStr = params.get('auth_date')
      if (!authDateStr) return null
      const ts = parseInt(authDateStr)
      return Number.isFinite(ts) ? ts : null
    } catch {
      return null
    }
  }

  /**
   * Проверка свежести initData. По требованиям Telegram максимум 3600 сек.
   */
  private isInitDataFresh(initData: string, maxAgeSeconds: number = 3600): boolean {
    const authTs = this.getAuthDateFromInitData(initData)
    if (!authTs) return false
    const now = Math.floor(Date.now() / 1000)
    const diff = now - authTs
    return diff >= 0 && diff <= maxAgeSeconds
  }

  /**
   * Определяет режим работы приложения на основе способа авторизации
   * КЛЮЧЕВАЯ ЛОГИКА: Платформа определяется по способу авторизации, а не по техническим признакам
   */
  private detectMode(): AuthMode {
    // 0) ПРИОРИТЕТ: Путь /miniapp ВСЕГДА означает Telegram режим
    const pathname = window.location.pathname
    if (pathname.startsWith('/miniapp')) {
      return 'telegram'
    }

    // 1) ВАЖНО: Проверяем наличие Telegram WebApp API (основной индикатор)
    const webApp = (window as any)?.Telegram?.WebApp
    const hasWebAppObject = !!webApp
    const hasRealWebAppData = !!(webApp && (webApp.initDataUnsafe?.user?.id || (webApp.initData && webApp.initData.length > 0)))
    
    if (hasWebAppObject || hasRealWebAppData) {
      return 'telegram'
    }

    // 2) Дополнительная проверка Telegram окружения
    const urlParams = new URLSearchParams(window.location.search)
    const userAgent = navigator.userAgent.toLowerCase()
    const hash = window.location.hash
    const hasTelegramUrlHints = !!(urlParams.get('tgWebAppPlatform') || urlParams.get('tgWebAppVersion') || hash.includes('tgWebAppData='))
    const isTelegramUA = userAgent.includes('telegram')
    
    if (hasTelegramUrlHints || isTelegramUA) {
      logger.log('📱 Telegram режим определен по URL подсказкам или User Agent', { hasTelegramUrlHints, isTelegramUA })
      return 'telegram'
    }

    // 2.1) Если ранее уже обнаружен и сохранен telegram_id — считаем, что это Telegram режим
    try {
      const savedTelegramId = localStorage.getItem('telegram_id')
      if (savedTelegramId) {
        logger.log('📱 Telegram режим определен по сохраненному telegram_id')
        return 'telegram'
      }
    } catch {}

    // 3) Если это не Telegram окружение — допускаем браузерный вход
    //    Проверяем быстрые признаки браузерной авторизации
    const webTokenFromUrl = urlParams.get('web_token')
    const savedWebToken = localStorage.getItem('alfastil_web_token')
    if (webTokenFromUrl || savedWebToken) {
      if (true) {
        logger.log('🌐 Режим browser: найден web_token', { fromUrl: !!webTokenFromUrl, saved: !!savedWebToken })
      }
      return 'browser'
    }

    // 4) В качестве последнего шага учитываем уже сохраненный JWT
    const savedToken = localStorage.getItem('alfastil_token')
    if (savedToken) {
      try {
        const parts = savedToken.split('.')
        if (parts.length === 3) {
          const payload = JSON.parse(atob(parts[1]))
          const now = Math.floor(Date.now() / 1000)
          if (payload.exp > now && payload.type === 'web_auth') {
            return 'browser'
          }
        }
      } catch {
        // игнорируем ошибки парсинга
      }
    }

    // 5) По умолчанию — браузер
    if (true) {
      logger.log('🌐 Режим по умолчанию: browser')
    }
    return 'browser'
  }

  /**
   * Получает initData из Telegram WebApp или URL
   */
  private getTelegramInitData(): string | null {
    logger.log('🔍 ДИАГНОСТИКА: Начинаем getTelegramInitData()')
    
    // Диагностика окружения
    logger.log('🔍 ДИАГНОСТИКА: Окружение', {
      url: window.location.href,
      pathname: window.location.pathname,
      search: window.location.search,
      hash: window.location.hash,
      userAgent: navigator.userAgent,
      hasTelegramObject: !!(window as any)?.Telegram,
      hasWebApp: !!(window as any)?.Telegram?.WebApp
    })
    // Метод 1 (ПРИОРИТЕТ): Из WebApp API, если доступен и данные свежие
    if (typeof window !== 'undefined' && (window as any).Telegram?.WebApp) {
      const webApp = (window as any).Telegram.WebApp
      const initData = webApp.initData || null
      
      logger.log('🔍 ДИАГНОСТИКА: WebApp API данные', {
        hasInitData: !!initData,
        initDataLength: initData?.length || 0,
        hasInitDataUnsafe: !!webApp.initDataUnsafe,
        userId: webApp.initDataUnsafe?.user?.id,
        version: webApp.version,
        platform: webApp.platform
      })
      
      // Сохраняем telegram_id для последующего использования
      if (webApp.initDataUnsafe?.user?.id) {
        const telegramId = String(webApp.initDataUnsafe.user.id)
        localStorage.setItem('telegram_id', telegramId)
        logger.log('👤 ДИАГНОСТИКА: telegram_id сохранен из WebApp API:', telegramId)
      }
      
      if (initData && initData.length > 0) {
        const fresh = this.isInitDataFresh(initData)
        if (fresh) {
          logger.log('✅ ДИАГНОСТИКА: Используем СВЕЖИЕ initData из WebApp API')
          // Сохраняем свежие данные для последующего использования
          localStorage.setItem('telegram_initData', initData)
          return initData
        } else {
          logger.warn('⚠️ ДИАГНОСТИКА: initData из WebApp API устарел, игнорируем')
        }
      } else {
        logger.log('⚠️ ДИАГНОСТИКА: WebApp API не предоставил initData или данные пустые')
      }
    } else {
      logger.log('⚠️ ДИАГНОСТИКА: Telegram WebApp API недоступен')
    }
    
    // Метод 2: Из URL параметров (fallback) — используем ТОЛЬКО если данные свежие
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search)
      const hash = window.location.hash
      
      logger.log('🔍 ДИАГНОСТИКА: Проверяем URL параметры', {
        searchParams: window.location.search,
        hash: window.location.hash,
        tgWebAppDataFromSearch: urlParams.get('tgWebAppData'),
        hashMatch: hash.match(/tgWebAppData=([^&]+)/)?.[1]
      })
      
      // Проверяем URL параметры
      const tgWebAppData = urlParams.get('tgWebAppData') || hash.match(/tgWebAppData=([^&]+)/)?.[1]
      if (tgWebAppData) {
        logger.log('✅ ДИАГНОСТИКА: Найден tgWebAppData в URL:', tgWebAppData.substring(0, 100) + '...')
        try {
          const decodedData = decodeURIComponent(tgWebAppData)
          logger.log('✅ ДИАГНОСТИКА: tgWebAppData успешно декодирован, длина:', decodedData.length)
          
          // Проверяем свежесть URL initData
          if (!this.isInitDataFresh(decodedData)) {
            logger.warn('⚠️ ДИАГНОСТИКА: URL initData устарел — игнорируем и НЕ сохраняем')
          } else {
            // Попытка извлечь telegram_id для сохранения
            try {
              const parsed = new URLSearchParams(decodedData)
              const userStr = parsed.get('user')
              if (userStr) {
                const user = JSON.parse(decodeURIComponent(userStr))
                if (user.id) {
                  const telegramId = String(user.id)
                  localStorage.setItem('telegram_id', telegramId)
                  logger.log('👤 ДИАГНОСТИКА: telegram_id извлечен из URL и сохранен:', telegramId)
                }
              }
            } catch (error) {
              logger.log('⚠️ ДИАГНОСТИКА: Ошибка извлечения telegram_id из URL данных:', error)
            }
            // Сохраняем только свежие URL initData
            localStorage.setItem('telegram_initData', decodedData)
            return decodedData
          }
        } catch (error) {
          logger.log('❌ ДИАГНОСТИКА: Ошибка декодирования tgWebAppData из URL:', error)
        }
      } else {
        logger.log('❌ ДИАГНОСТИКА: tgWebAppData НЕ найден в URL')
      }
    }
    
    // Метод 3: Проверяем сохраненный initData в localStorage, используем ТОЛЬКО если свежий
    try {
      const savedInitData = localStorage.getItem('telegram_initData')
      if (savedInitData) {
        if (this.isInitDataFresh(savedInitData)) {
          logger.log('✅ ДИАГНОСТИКА: Используем свежий initData из localStorage')
          return savedInitData
        } else {
          logger.warn('⚠️ ДИАГНОСТИКА: Сохраненный initData устарел — очищаем')
          localStorage.removeItem('telegram_initData')
        }
      }
    } catch (error) {
      logger.log('⚠️ ДИАГНОСТИКА: Ошибка чтения localStorage при проверке сохраненного initData:', error)
    }

    // Метод 4: Попытка создать фиктивный initData для тестирования (ТОЛЬКО В DEV)
    if (
      typeof window !== 'undefined' &&
      window.location.pathname.startsWith('/miniapp') &&
      (typeof import.meta !== 'undefined' && (import.meta as any)?.env?.DEV)
    ) {
      const savedTelegramId = localStorage.getItem('telegram_id')
      if (savedTelegramId) {
        if (true) {
          logger.log('🔄 Используем сохраненный telegram_id для создания фиктивного initData:', savedTelegramId)
        }
        
        // Создаем минимальный initData для авторизации
        const fakeUser = {
          id: parseInt(savedTelegramId),
          first_name: 'User',
          username: 'user' + savedTelegramId
        }
        
        const fakeInitData = new URLSearchParams({
          user: JSON.stringify(fakeUser),
          auth_date: Math.floor(Date.now() / 1000).toString(),
          query_id: 'fake_query_id'
        }).toString()
        
        if (true) {
          logger.log('🔧 Создан фиктивный initData для сохраненного telegram_id')
        }
        
        return fakeInitData
      }
    }
    
    if (true) {
      logger.log('❌ initData не найден ни одним из методов')
    }
    return null
  }
  
  /**
   * Извлекает telegram_id из URL данных
   */
  private extractTelegramIdFromUrl(tgWebAppData: string): string | null {
    try {
      const params = new URLSearchParams(tgWebAppData)
      const userParam = params.get('user')
      if (userParam) {
        const user = JSON.parse(decodeURIComponent(userParam))
        return String(user.id) || null
      }
    } catch (e) {
      logger.warn('Ошибка при извлечении telegram_id из URL данных:', e)
    }
    return null
  }

  /**
   * Получает web_token из URL или localStorage
   */
  private getWebToken(): string | null {
    // Сначала проверяем URL
    const urlParams = new URLSearchParams(window.location.search)
    const webToken = urlParams.get('web_token')
    
    if (webToken) {
      // Сохраняем в localStorage для последующих сессий
      localStorage.setItem('alfastil_web_token', webToken)
      return webToken
    }
    
    // Fallback к сохраненному токену
    return localStorage.getItem('alfastil_web_token')
  }

  /**
   * Авторизация через Telegram initData
   */
  private async authenticateWithTelegram(initData: string): Promise<{
    success: boolean
    user?: User
    token?: string
    message?: string
    needs_registration?: boolean
    pending_approval?: boolean
  }> {
    try {
      // Авторизация через Telegram initData
      
      const isDebug = true
      const response = await authWithInitData(initData, isDebug)
      
              if (response?.success && response?.token && response?.user) {
          // Авторизация успешна
          
          // Сохраняем telegram_id для правильного определения режима
          if (response.user.id) {
            localStorage.setItem('telegram_id', String(response.user.id))
          }
          
          return {
            success: true,
            user: response.user,
            token: response.token
          }
      }
      
      if (true) {
        logger.warn('❌ Telegram авторизация не удалась:', response?.message)
        logger.log('🔍 ОТЛАДКА: Полный ответ от /auth:', response)
        logger.log('🔍 ОТЛАДКА: needs_registration =', (response as any)?.needs_registration)
        logger.log('🔍 ОТЛАДКА: pending_approval =', (response as any)?.pending_approval)
      }
      return {
        success: false,
        message: response?.message || 'Ошибка авторизации',
        needs_registration: (response as any)?.needs_registration,
        pending_approval: (response as any)?.pending_approval
      }
      
    } catch (error) {
      logger.error('❌ Ошибка при Telegram авторизации:', error)
      return {
        success: false,
        message: 'Произошла ошибка при авторизации'
      }
    }
  }

  /**
   * Авторизация через web_token
   */
  private async authenticateWithWebToken(webToken: string): Promise<{
    success: boolean
    user?: User
    token?: string
    message?: string
    needs_registration?: boolean
    pending_approval?: boolean
  }> {
    try {
      if (true) {
        logger.log('🌐 Браузерная авторизация через web_token...')
      }
      
      // Отправляем запрос на backend с web_token  
      const { env } = await import('../../config/env')
      const apiUrl = env.resolveApiUrl()
      
      const response = await fetch(`${apiUrl}/web_auth/auth/web`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ web_token: webToken })
      })
      
      const data = await response.json()
      
      if (data?.success && data?.token && data?.user) {
        if (true) {
          logger.log('✅ Браузерная авторизация успешна')
        }
        return {
          success: true,
          user: data.user,
          token: data.token
        }
      }
      
      if (true) {
        logger.warn('❌ Браузерная авторизация не удалась:', data?.message)
      }
      return {
        success: false,
        message: data?.message || 'Ошибка авторизации',
        needs_registration: data?.needs_registration,
        pending_approval: data?.pending_approval
      }
      
    } catch (error) {
      logger.error('❌ Ошибка при браузерной авторизации:', error)
      return {
        success: false,
        message: 'Произошла ошибка при авторизации'
      }
    }
  }

  /**
   * Главный метод инициализации авторизации
   */
  async initializeAuth(): Promise<boolean> {
    try {
      if (true) {
        logger.log('🚀 Начинаем initializeAuth...')
      }
      this.authStore.setStatus('loading')
      
      // Определяем режим работы
      const mode = this.detectMode()
      if (true) {
        logger.log('🔍 Определен режим:', mode)
      }
      
      // Обновляем режим в store правильно
      useAuthStore.setState({ mode })
      
      if (mode === 'telegram') {
        return await this.initializeTelegramAuth()
      } else if (mode === 'browser') {
        return await this.initializeBrowserAuth()
      } else {
        // Fallback к браузерному режиму
        logger.log('🔄 Fallback к браузерному режиму')
        useAuthStore.setState({ mode: 'browser' })
        return await this.initializeBrowserAuth()
      }
      
    } catch (error) {
      logger.error('❌ Ошибка при инициализации авторизации:', error)
      this.authStore.setStatus('unauthenticated')
      this.authStore.setError('Ошибка инициализации авторизации')
      return false
    }
  }

  /**
   * Инициализация Telegram авторизации
   */
  public async initializeTelegramAuth(): Promise<boolean> {
    if (true) {
      logger.log('📱 Начинаем инициализацию Telegram авторизации...')
    }
    
    // КРИТИЧНО: Ждем загрузки Telegram WebApp API
    try {
      const { initTelegramWebAppIfNeeded } = await import('../utils/telegramLoader')
      const telegramLoaded = await initTelegramWebAppIfNeeded()
      logger.log('📱 ДИАГНОСТИКА: Telegram WebApp API загружен:', telegramLoaded)
      
      // Даем время API инициализироваться
      if (telegramLoaded) {
        await new Promise(resolve => setTimeout(resolve, 100))
      }
    } catch (error) {
      logger.warn('⚠️ ДИАГНОСТИКА: Ошибка загрузки Telegram WebApp API:', error)
    }
    
    const initData = this.getTelegramInitData()
    if (true) {
      logger.log('📱 Получены initData:', initData ? 'есть данные' : 'нет данных')
    }
    
    if (!initData) {
      if (true) {
        logger.log('❌ Нет initData, устанавливаем статус unauthenticated')
      }
      this.authStore.setStatus('unauthenticated')
      this.authStore.setError('Данные Telegram WebApp недоступны. Попробуйте перезапустить приложение.')
      return false
    }
    
    // ВАЖНО: Для MiniApp ВСЕГДА проверяем актуальный статус в Bitrix,
    // даже если есть сохраненный JWT токен. Это критично для безопасности:
    // - Админ может удалить контакт
    // - Может быть отозван доступ к боту
    // - Статус пользователя может измениться
    if (true) {
      logger.log('🔐 MiniApp: Проверяем актуальный статус в Bitrix (игнорируем сохраненный токен)')
    }
    
    if (true) {
      logger.log('📱 Пытаемся авторизоваться с initData...')
    }
    
    // Сохраняем Telegram данные
    const telegramId = this.extractTelegramId(initData)
    if (true) {
      logger.log('📱 Извлечен telegram_id:', telegramId)
      logger.log('📱 Отправляем запрос авторизации на backend...')
    }
    
    // ВАЖНО: Сохраняем initData в localStorage для использования при переходах между страницами MiniApp
    localStorage.setItem('telegram_initData', initData)
    
    this.authStore.setTelegramData(telegramId, initData)
    
    const result = await this.authenticateWithTelegram(initData)
    if (true) {
      logger.log('📱 Результат авторизации:', result.success ? 'успех' : 'ошибка', result.message)
    }
    
    if (result.success && result.user && result.token) {
      // ВАЖНО: Для MiniApp всегда устанавливаем режим 'telegram', 
      // даже если есть client_id (ID контакта в Bitrix)
      const finalMode = 'telegram'
      
      if (true) {
        logger.log('📱 Устанавливаем режим для MiniApp:', finalMode)
        logger.log('📱 user.client_id:', result.user.client_id)
        logger.log('📱 user.id:', result.user.id)
      }
      
      this.authStore.setAuth(result.token, result.user, finalMode)
      return true
    } else {
      if (true) {
        logger.log('🔍 ОТЛАДКА: Устанавливаем состояние после неудачной авторизации')
        logger.log('🔍 ОТЛАДКА: result.needs_registration =', result.needs_registration)
        logger.log('🔍 ОТЛАДКА: result.pending_approval =', result.pending_approval)
        logger.log('🔍 ОТЛАДКА: result.message =', result.message)
      }
      
      this.authStore.setStatus('unauthenticated')
      this.authStore.setError(result.message || 'Ошибка авторизации')
      
      if (result.needs_registration) {
        if (true) {
          logger.log('✅ ОТЛАДКА: Вызываем setNeedsRegistration(true)')
        }
        this.authStore.setNeedsRegistration(true)
      }
      
      if (result.pending_approval) {
        if (true) {
          logger.log('✅ ОТЛАДКА: Вызываем setPendingApproval(true)')
        }
        this.authStore.setPendingApproval(true)
      }
      
      return false
    }
  }

  /**
   * Инициализация браузерной авторизации
   */
  public async initializeBrowserAuth(): Promise<boolean> {
    // Проверяем сохраненный токен из предыдущей сессии
    const savedToken = localStorage.getItem('alfastil_token') || localStorage.getItem('alfastil_web_token')
    
    if (savedToken) {
      if (true) {
        logger.log('🔄 Найден сохраненный токен, проверяем его валидность...')
      }
      
      try {
        if (true) {
          logger.log('🔍 Проверяем токен:', savedToken.substring(0, 50) + '...')
        }
        // Проверяем токен через API  
        const response = await fetch(`${await this.getApiUrl()}/web_auth/me`, {
          headers: {
            'Authorization': `Bearer ${savedToken}`,
            'Content-Type': 'application/json'
          }
        })
        
        if (response.ok) {
          const data = await response.json()
          
          if (data?.success && data?.user) {
            if (true) {
              logger.log('✅ Сохраненный токен валиден, восстанавливаем сессию')
            }
            // Определяем режим по данным user
            const finalMode: AuthMode = data.user?.client_id ? 'browser' : 'telegram'
            this.authStore.setAuth(savedToken, data.user, finalMode)
            return true
          } else {
            if (true) {
              logger.warn('⚠️ Сохраненный токен недействителен (неверный формат ответа), очищаем')
            }
            this.clearStoredAuth()
          }
        } else if (response.status === 401) {
          const errorData = await response.json().catch(() => ({}))
          if (true) {
            logger.warn('⚠️ Сохраненный токен истек (401), очищаем. Ошибка:', errorData)
          }
          this.clearStoredAuth()
        } else {
          const errorData = await response.json().catch(() => ({}))
          if (true) {
            logger.warn(`⚠️ Ошибка проверки токена (${response.status}), очищаем. Ошибка:`, errorData)
          }
          this.clearStoredAuth()
        }
      } catch (error) {
        if (true) {
          logger.warn('⚠️ Ошибка сети при проверке токена:', error)
          // НЕ очищаем токен при ошибке сети - возможно, сервер недоступен
          logger.log('ℹ️ Оставляем токен, возможно проблемы с сетью')
        }
        this.authStore.setStatus('unauthenticated')
        return false
      }
    }
    
    // Если нет сохраненного токена или он недействителен - это нормально для первого посещения
    if (true) {
      logger.log('ℹ️ Требуется авторизация через форму входа')
    }
    this.authStore.setStatus('unauthenticated')
    return false
  }

  /**
   * Очистка сохраненных данных авторизации
   */
  private clearStoredAuth(): void {
    localStorage.removeItem('alfastil_token')
    localStorage.removeItem('alfastil_web_token')
    localStorage.removeItem('alfastil_user')
    localStorage.removeItem('telegram_id')
  }

  /**
   * Получение API URL
   */
  private async getApiUrl(): Promise<string> {
    if (typeof window !== 'undefined') {
      const { env } = await import('../../config/env')
      return env.resolveApiUrl()
    }
    return 'http://localhost:7070'
  }

  /**
   * Извлекает Telegram ID из initData
   */
  private extractTelegramId(initData: string): string {
    try {
      const params = new URLSearchParams(initData)
      const userStr = params.get('user')
      
      if (userStr) {
        const user = JSON.parse(userStr)
        return String(user.id || '')
      }
      
      return ''
    } catch (error) {
      logger.error('Ошибка при извлечении Telegram ID:', error)
      return ''
    }
  }

  /**
   * Выход из системы
   */
  logout(): void {
    if (true) {
      logger.log('🚪 Выход из системы')
    }
    this.authStore.clear()
    
    // Для браузерного режима очищаем URL от web_token
    if (typeof window !== 'undefined' && window.location.search.includes('web_token')) {
      const url = new URL(window.location.href)
      url.searchParams.delete('web_token')
      window.history.replaceState({}, document.title, url.pathname)
    }
  }

  /**
   * Проверка текущего статуса авторизации
   */
  isAuthenticated(): boolean {
    return this.authStore.isAuthenticated()
  }

  /**
   * Получение текущего пользователя
   */
  getCurrentUser(): User | null {
    return this.authStore.user
  }

  /**
   * Получение текущего токена
   */
  getCurrentToken(): string | null {
    return this.authStore.token
  }
}

// Экспортируем singleton экземпляр
export const authService = new AuthService()

// Удобная функция для инициализации авторизации в React компонентах
export async function initAuth(): Promise<boolean> {
  return authService.initializeAuth()
}
